import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { startWith } from 'rxjs';
import { ApiKeyStatusFilter, ApiKeyStatusRow } from './api-key-status-table.models';

@Component({
  selector: 'tgrid-api-key-status-table',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormField,
    MatLabel,
    MatInput,
  ],
  templateUrl: './api-key-status-table.component.html',
  styleUrl: './api-key-status-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApiKeyStatusTableComponent {
  readonly rows = input.required<ApiKeyStatusRow[]>();
  readonly loading = input(false);
  readonly errorMessage = input<string | null>(null);

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly selectedFilter = signal<ApiKeyStatusFilter>('all');
  protected readonly expandedUserId = signal<number | null>(null);
  private readonly searchTerm = toSignal(
    this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
    { initialValue: this.searchControl.value },
  );

  protected readonly filteredRows = computed(() => {
    const normalizedSearch = this.searchTerm().trim().toLowerCase();
    const filter = this.selectedFilter();

    return this.rows()
      .map((row) => {
        const details = row.details.filter((detail) => this.matchesFilter(detail.status, filter));

        return {
          ...row,
          apiKeyCount: details.length,
          details,
        };
      })
      .filter((row) =>
        row.details.length > 0 && row.userName.toLowerCase().includes(normalizedSearch)
      );
  });

  private matchesFilter(status: string, filter: ApiKeyStatusFilter): boolean {
    if (filter === 'all') {
      return true;
    }

    if (filter === 'active') {
      return status === 'Active';
    }

    return status === 'Expired' || status === 'Denied';
  }

  protected setStatusFilter(filter: ApiKeyStatusFilter): void {
    this.selectedFilter.set(filter);
    this.expandedUserId.set(null);
  }

  protected toggleExpanded(userId: number): void {
    this.expandedUserId.update((current) => (current === userId ? null : userId));
  }

  protected isExpanded(userId: number): boolean {
    return this.expandedUserId() === userId;
  }

  protected formatSupportedSystems(supportedSystems: string[]): string {
    return supportedSystems.length > 0 ? supportedSystems.join(', ') : 'N/A';
  }
}



